import { AxiosError } from 'axios'
import api from '@/api/axiosApi'
import type { PaginationMeta } from '@/types'
import type { CommentRead, PaginatedCommentsResponse } from '@/api/comments'

// Percentages in this API are 0-100. Content edits send the inputs_version the
// edited data came from as If-Match (428 when missing, 409 when stale).

export type WavePlanStatusValue =
  | 'NOT_STARTED'
  | 'DRAFT'
  | 'IN_PROGRESS'
  | 'SUBMITTED'
  | 'APPROVED'
  | 'SENT_BACK'
export type CalcPath = 'SEASONAL' | 'CORE'
export type WaveSkuLevel = 'SINGLE' | 'PARENT' | 'CHILD' | 'REPLEN'
export type WaveCellSource =
  | 'DEFAULT'
  | 'OVERRIDE_PERCENT'
  | 'OVERRIDE_QUANTITY'
  | 'INELIGIBLE'
export type FlagSeverity = 'INFO' | 'WARNING' | 'ERROR'
export type FlagScope = 'PLAN' | 'LINE' | 'WEEK' | 'DC'

export interface WavePlanFlag {
  code: string
  severity: FlagSeverity
  scope: FlagScope
  message: string
  line_ref_id: string | null
  week_id: string | null
  dc_id: string | null
  value: number | null
  threshold: number | null
  blocks_submission: boolean
}

export interface NamedRef {
  id: string
  name: string
}

export interface WavePlanUserRef {
  id: string
  name: string | null
  email: string | null
}

export interface WavePlanWeekHeader {
  id: string
  sequence: number
  fiscal_year: number
  fiscal_week: number
  event_code: string | null
  date: string | null
  is_wave_eligible: boolean
  default_percent: number
  outside: boolean
  /** SKU-calculated (bottoms-up) share of the buy in this week (null: frozen before 1.5.0). */
  sku_percent?: number | null
}

// --- list / detail ---------------------------------------------------------

export interface WavePlanListItem {
  id: string | null
  assortment_plan_id: string
  assortment_plan_name: string
  assortment_plan_status: string
  plan_type: string | null
  status: WavePlanStatusValue
  calc_path: CalcPath | null
  fiscal_year: number | null
  season_id: string | null
  season_name: string | null
  hierarchy_id: string | null
  set_reset_date: string | null
  mfps: NamedRef[]
  sku_count: number | null
  is_stale: boolean | null
  inputs_version: number | null
  owner_id: string | null
  updated_at: string | null
}

export interface WavePlanListResponse {
  data: WavePlanListItem[]
  pagination: PaginationMeta
}

export interface WavePlanListParams {
  offset?: number
  limit?: number
  search?: string
  status?: WavePlanStatusValue
  fiscal_year?: number
  include_not_started?: boolean
}

export interface WavePlanHeader {
  store_count: number
  sku_count: number
  sellable_count: number
  shipping_count: number
  piab_count: number
  repack_count: number
  dc_count: number
  pog_set: number
  pog_max: number | null
  purchase_units: number
  total_qty: number
}

export interface WavePlanSummary {
  total_percent: number
  balanced: boolean
  minimum_first_wave_percent: number
  first_wave_percent: number
  below_minimum: boolean
  in_season_demand_percent: number
  average_store_receipts: number
  average_store_sales: number
  /** Plan store count the overall model uses (largest store model by default). */
  store_count?: number
  /** Receipt-to-sales threshold; null when the trigger is off (e.g. Core without demand). */
  variance_threshold_percent?: number | null
  /** Recommended next wave: variance below the threshold while the plan is < 100%. */
  next_wave_week_id?: string | null
  set_week_id?: string | null
  set_event_code?: string | null
  /**
   * Whether the planner's weekly percentages drive the plan (false only for a Core plan
   * whose initial/backup split comes from the assortment plan per SKU).
   */
  default_editable?: boolean
  /** Core plan on the AP's per-SKU quantities: its two receipts (null otherwise). */
  core_receipts?: WavePlanCoreReceipts | null
  /** Distribution behind receipts, inventory, the variance and the next-wave timing. */
  metrics_basis?: 'sku_bottoms_up' | 'plan_default' | null
  /** SKU-calculated (bottoms-up) total of the waves. */
  sku_total_percent?: number | null
  /** Plan-level percentage of the first wave week (``first_wave_percent`` is SKU-calculated). */
  planned_first_wave_percent?: number | null
  first_wave_week_id?: string | null
  /** The SKUs' POG set requirements (each SKU's stores, capped at its buy) / their buy. */
  pog_set_minimum_percent?: number | null
  /** SKUs whose first wave ships less than their POG set (a warning). */
  below_pog_set_count?: number
}

export type WavePlanReceiptSource = 'PLANNER' | 'SUGGESTED'

export interface WavePlanCoreReceipts {
  /** Effective weeks; null when no week can be suggested or used. */
  initial_week_id: string | null
  /** May equal the initial week. */
  backup_week_id: string | null
  initial_source: WavePlanReceiptSource
  backup_source: WavePlanReceiptSource
  initial_qty: number
  backup_qty: number
  /** The planner's stored choices (null: not chosen); a chosen week that can't
   * receive a wave falls back to the suggestion until it can again. */
  chosen_initial_week_id?: string | null
  chosen_backup_week_id?: string | null
}

export interface WavePlanPermissions {
  can_edit: boolean
  can_refresh: boolean
  can_submit: boolean
  can_withdraw: boolean
  can_review: boolean
  can_unapprove: boolean
}

export interface WavePlanSubmissionReviewer extends WavePlanUserRef {
  position: number
  seen_by_approver: boolean
}

export interface WavePlanSubmission {
  id: string
  wave_plan_id: string
  assortment_plan_id: string
  assortment_plan_name: string | null
  status: string
  notes: string | null
  submitted_at: string
  submitted_by: WavePlanUserRef
  reviewers: WavePlanSubmissionReviewer[]
  decided_at: string | null
  decided_by: WavePlanUserRef | null
  decision_notes: string | null
  revision_id: string
  inputs_version: number
  engine_version: string
  is_stale: boolean | null
}

export interface WavePlanDetail {
  id: string
  assortment_plan_id: string
  assortment_plan_name: string
  assortment_plan_status: string
  status: WavePlanStatusValue
  calc_path: CalcPath
  inputs_version: number
  is_stale: boolean
  stale_reasons: string[]
  source_version_id: string | null
  refreshed_at: string | null
  population_as_of: string | null
  capture_params: Record<string, unknown>
  ap_header: {
    set_reset_date: string | null
    exit_date: string | null
    calendar_event_id: string | null
    seasonal_calendar_event_id: string | null
    fiscal_year: number | null
    season_id: string | null
    hierarchy_id: string | null
    plan_type: string | null
  }
  header: WavePlanHeader
  summary: WavePlanSummary
  mfps: NamedRef[]
  hierarchy: {
    division: string | null
    department: string | null
    class: string | null
    subclass: string | null
  } | null
  season_name: string | null
  flags: WavePlanFlag[]
  flag_counts: Record<string, number>
  blocking_flag_count: number
  current_submission: WavePlanSubmission | null
  revision_id: string | null
  engine_version: string
  reviewer_roles: string[]
  permissions: WavePlanPermissions
  notes: string | null
  created_at: string
  updated_at: string
}

// --- weeks -----------------------------------------------------------------

export interface WavePlanWeek {
  id: string
  sequence: number
  fiscal_year: number
  fiscal_week: number
  event_code: string | null
  week_start_date: string
  receipt_date: string | null
  ship_date: string | null
  captured_receipt_date: string | null
  captured_ship_date: string | null
  planner_receipt_date: string | null
  planner_ship_date: string | null
  is_wave_eligible: boolean
  removed: boolean
  default_percent: number | null
  planned_percent: number
  ly_wave_percent: number
  ly_demand_units: number
  cumulative_percent: number
  cumulative_demand_percent: number
  receipts: number
  average_receipts: number
  inventory: number
  average_inventory: number
  receipts_in_stores_percent: number
  sales_in_stores_percent: number
  variance_percent: number
  /** First week after a wave where the variance is below the threshold. */
  threshold_crossed?: boolean
  /** Event code sent downstream with a wave in this week. */
  flow_event_code?: string | null
  /** SKU-calculated share of the buy in this week / through it (null: frozen before 1.5.0). */
  sku_percent?: number | null
  sku_cumulative_percent?: number | null
}

export interface WavePlanWeeksResponse {
  inputs_version: number
  weeks: WavePlanWeek[]
  summary: WavePlanSummary
  flags: WavePlanFlag[]
}

export interface WavePlanWeekInput {
  week_id: string
  default_percent?: number | null
  receipt_date?: string | null
  ship_date?: string | null
}

// --- lines -----------------------------------------------------------------

export interface WavePlanLineCell {
  week_id: string
  percent: number
  qty: number
  avg_store: number | null
  cube_per_dc: number | null
  source: WaveCellSource
  /** What the system plans for the cell without planner overrides. */
  system_qty?: number | null
  /** The wave after DC rounding; the cells add up to ``wp_final_quantity`` (null: unknown). */
  final_qty?: number | null
}

export interface WavePlanLine {
  line_ref_id: string
  line_number: number | null
  sku_number: string | null
  /** The AP's plan SKU # (a new SKU has it before its SKU number). */
  plan_sku_num?: string | null
  description: string | null
  sku_level: WaveSkuLevel
  sku_type: string | null
  parent_line_ref_id: string | null
  vendor_code: string | null
  vendor_name: string | null
  store_count: number
  ap_store_count: number | null
  case_pack: number | null
  inner_pack: number | null
  case_pack_cube: number | null
  ip_cube: number | null
  moq: number | null
  moa: number | null
  base_quantity: number
  pog_set_qty: number | null
  pog_max_qty: number | null
  unit_cost: number | null
  unit_retail: number | null
  piab: string | null
  conveyable: string | null
  ship_type: string | null
  buy_strategy: string | null
  dc_count: number
  total_qty: number
  allocated_qty: number
  total_percent: number
  has_custom_distribution: boolean
  outside_week_ids: string[]
  needs_review: boolean
  cells: WavePlanLineCell[]
  comment_count: number
  flags: WavePlanFlag[]
  /** AP quantity the line schedules (Core: initial + backup). */
  ap_quantity?: number
  /** Total Wave Quantity: DC shares rounded up to the pack, over every wave (null: no DCs). */
  wp_final_quantity?: number | null
  variance_units?: number | null
  variance_percent?: number | null
}

export interface WavePlanTotals {
  cells: {
    week_id: string
    percent: number | null
    qty: number
    avg_store: number | null
    cube_per_dc: number | null
    final_qty?: number | null
  }[]
  total_qty: number
  allocated_qty: number
  total_percent: number | null
  needs_review: boolean
  wp_final_quantity?: number | null
  variance_units?: number | null
  variance_percent?: number | null
}

export interface WavePlanLinesResponse {
  inputs_version: number
  weeks: WavePlanWeekHeader[]
  lines: WavePlanLine[]
  totals: WavePlanTotals
}

export interface WavePlanSkuWeekInput {
  week_id: string
  percent?: number | null
  quantity?: number | null
  receipt_date?: string | null
  ship_date?: string | null
  note?: string | null
}

// --- DC --------------------------------------------------------------------

export interface WavePlanDcRef {
  id: string
  code: string
  name: string
  /** Routing placeholder (e.g. "Single DC Strategy"), not a real DC: read-only. */
  virtual?: boolean
}

export interface WavePlanDcCell {
  dc_id: string
  percent: number
  raw: number
  adjusted: number
  delta: number
  source: string
  virtual?: boolean
  /** PO cost of the adjusted units (a Child's PIAB units are on the parent's PO); null: unknown. */
  po_cost?: number | null
}

export interface WavePlanDcRow {
  line_ref_id: string
  line_number: number | null
  sku_number: string | null
  description: string | null
  sku_level: WaveSkuLevel
  vendor_name: string | null
  multiple: number
  moq: number
  wave_qty: number
  distribution_qty: number
  moq_delta: number
  eligible_dc_ids: string[]
  cells: WavePlanDcCell[]
  percent: number
  raw: number
  adjusted: number
  delta: number
  balance: number
  balanced: boolean
  needs_review: boolean
  source_needs_review: boolean
  has_override: boolean
  other_week_ids: string[]
  /** The line's store model (null: none). */
  store_model_id?: string | null
  store_model_name?: string | null
  vendor_id?: string | null
  vendor_code?: string | null
  /** Captured unit cost (FOB pre-tariff by default). */
  unit_cost?: number | null
  /** Sum of the cells' PO cost (null when none is known). */
  po_cost?: number | null
  /** The wave is above 0 and below the line's MOQ (a ``BELOW_MOQ`` warning; never raised). */
  below_moq?: boolean
  /** Core routing: a conveyable case ships to one DC, a non-conveyable one is split; null: unknown. */
  conveyable?: 'Yes' | 'No' | null
  /** Conveyable unknown (no case dimensions): split across the line's DCs by default. */
  routing_defaulted?: boolean
  /** No store model: planned on the AP's Future Store Count and every DC's share of all stores. */
  no_store_model?: boolean
}

/** A vendor's PO at one DC over the covered waves. */
export interface WavePlanDcVendorCenter {
  dc_id: string
  units: number
  cost: number
  /** Covered waves whose PO here is above 0 and below the vendor MOA. */
  below_moa_waves?: number
  /** Covered waves not checked: a line with units but no cost. */
  moa_unchecked_waves?: number
}

/** A vendor's PO rollup by DC (units exclude Parent; cost includes the PIAB packs). */
export interface WavePlanDcVendorTotals {
  /** vendor_id, else vendor_code, else vendor_name; '' = no vendor. */
  key: string
  vendor_id: string | null
  vendor_code: string | null
  vendor_name: string | null
  line_count: number
  units: number
  cost: number
  /** Lines with units but no PO cost (no unit cost, or a Child without PIAB contents). */
  cost_missing_count: number
  /** The vendor's MOA $ (most frequent of its lines' by default); null: none or not checked. */
  moa?: number | null
  centers: WavePlanDcVendorCenter[]
}

export interface WavePlanDcTotals {
  wave_qty: number
  raw: number
  adjusted: number
  delta: number
  balance: number
  excess: number
  excess_count: number
  excess_percent: number
  centers: (WavePlanDcRef & { raw: number; adjusted: number })[]
}

export interface WavePlanDcResponse {
  inputs_version: number
  /** Null while the plan has no wave weeks. */
  week: WavePlanWeekHeader | null
  weeks: WavePlanWeekHeader[]
  centers: WavePlanDcRef[]
  rows: WavePlanDcRow[]
  totals: WavePlanDcTotals
  all_weeks_totals: WavePlanDcTotals
  vendor_totals?: WavePlanDcVendorTotals[]
  all_weeks_vendor_totals?: WavePlanDcVendorTotals[]
}

export interface WavePlanDcSplitInput {
  dc_id: string
  percent?: number | null
  quantity?: number | null
}

export type WavePlanDcBulkRequest =
  | {
      op: 'set_dc_percent'
      line_ref_ids: string[]
      week_id: string
      dc_id: string
      percent: number
    }
  | {
      op: 'copy'
      line_ref_ids: string[]
      source_week_id: string
      target_week_ids: string[]
    }
  | {
      op: 'apply_split'
      line_ref_ids: string[]
      target_week_ids: string[]
      splits: WavePlanDcSplitInput[]
    }

export interface WavePlanDcBulkResponse {
  inputs_version: number
  applied: number
  skipped: { line_ref_id: string; reason: string }[]
}

export interface InputsVersionResponse {
  inputs_version: number
}

/** Bulk SKU edits: one edit and one undo step for many SKUs. */
export type WavePlanSkuBulkRequest =
  | {
      op: 'set_weeks'
      lines: { line_ref_id: string; weeks: WavePlanSkuWeekInput[] }[]
    }
  | { op: 'reset'; line_ref_ids: string[] }

export type WavePlanSkuBulkResponse = WavePlanDcBulkResponse

/** One of the user's undoable edits of a plan (most recent first). */
export interface WavePlanUndoEntry {
  id: string
  change_type: string
  description: string
  created_at: string
}

export interface WavePlanUndoStack {
  entries: WavePlanUndoEntry[]
}

export interface WavePlanUndoResult {
  undone: boolean
  description: string
  inputs_version: number
}

// --- refresh / submissions / rollup ----------------------------------------

export interface FieldChange {
  old: unknown
  new: unknown
}

export interface WavePlanLineRef {
  line_ref_id: string
  sku_number: string | null
  description: string | null
}

export interface WavePlanRefreshDiff {
  source_version_changed: boolean
  header: Record<string, FieldChange>
  capture_params: Record<string, FieldChange>
  added_lines: WavePlanLineRef[]
  removed_lines: WavePlanLineRef[]
  changed_lines: (WavePlanLineRef & { fields: Record<string, FieldChange> })[]
  population: {
    groups_added: string[]
    groups_removed: string[]
    stores_added: number
    stores_removed: number
    stores_moved_dc: number
    lines_with_changed_groups: string[]
  }
  timeline: {
    weeks_added: string[]
    weeks_removed: string[]
    weeks_changed: string[]
  }
  material: boolean
  material_reasons: string[]
}

export interface WavePlanRefreshPreview {
  inputs_version: number
  snapshot_hash: string | null
  current_hash: string
  diff: WavePlanRefreshDiff
}

export interface WavePlanRefreshResponse {
  inputs_version: number
  revision_id: string
  snapshot_hash: string
  diff: WavePlanRefreshDiff
}

export interface WavePlanRollupMetrics {
  percent: number | null
  units: number
  sku_count: number | null
  repack_sku_count: number | null
  cube: number | null
}

export interface WavePlanRollupWeek extends WavePlanRollupMetrics {
  week_key: string
  fiscal_year: number
  fiscal_week: number
  date: string | null
  event_code: string | null
}

export interface WavePlanRollupPlan {
  wave_plan_id: string
  assortment_plan_id: string
  name: string
  status: WavePlanStatusValue
  set_reset_date: string | null
  base_units: number
  wave_count: number
  needs_review: boolean
  active_week_keys: string[]
  weeks: WavePlanRollupWeek[]
  season: WavePlanRollupMetrics
  header: WavePlanHeader | null
}

export interface WavePlanRollupGroup {
  fiscal_year: number
  weeks: WavePlanRollupWeek[]
  active_week_keys: string[]
  plans: WavePlanRollupPlan[]
  base_units: number
  season: WavePlanRollupMetrics
}

export interface WavePlanRollupResponse {
  groups: WavePlanRollupGroup[]
  missing_ids: string[]
}

// --- requests ---------------------------------------------------------------

const ifMatch = (inputsVersion: number) => ({
  headers: { 'If-Match': `"${inputsVersion}"` },
})

export async function fetchWavePlans(
  params: WavePlanListParams
): Promise<WavePlanListResponse> {
  const response = await api.get<WavePlanListResponse>('/wave-plans', {
    params,
  })
  return response.data
}

export async function createWavePlan(
  assortmentPlanId: string
): Promise<WavePlanDetail> {
  const response = await api.post<WavePlanDetail>('/wave-plans', {
    assortment_plan_id: assortmentPlanId,
  })
  return response.data
}

export async function fetchWavePlan(id: string): Promise<WavePlanDetail> {
  const response = await api.get<WavePlanDetail>(`/wave-plans/${id}`)
  return response.data
}

// --- active parameters (information drawer) ----------------------------------

export type WaveParamGroup = 'WP_SEASONAL' | 'WP_CORE' | 'WP_GENERAL'

export interface WavePlanActiveParameter {
  key: string
  group: WaveParamGroup
  description: string
  value: unknown
  default: unknown
  is_default: boolean
  scoped_overrides: number
}

export interface WavePlanActiveParameters {
  calc_path: CalcPath
  /** Values frozen with the plan's revision (submitted/approved). */
  frozen: boolean
  as_of: string | null
  parameters: WavePlanActiveParameter[]
}

export async function fetchWavePlanParameters(
  id: string
): Promise<WavePlanActiveParameters> {
  const response = await api.get<WavePlanActiveParameters>(
    `/wave-plans/${id}/parameters`
  )
  return response.data
}

export async function fetchWavePlanWeeks(
  id: string
): Promise<WavePlanWeeksResponse> {
  const response = await api.get<WavePlanWeeksResponse>(
    `/wave-plans/${id}/weeks`
  )
  return response.data
}

export async function updateWavePlanWeeks(
  id: string,
  weeks: WavePlanWeekInput[],
  inputsVersion: number
): Promise<WavePlanWeeksResponse> {
  const response = await api.put<WavePlanWeeksResponse>(
    `/wave-plans/${id}/weeks`,
    { weeks },
    ifMatch(inputsVersion)
  )
  return response.data
}

/** The planner's Core receipt weeks; null restores the suggested week. */
export async function updateWavePlanCoreReceipts(
  id: string,
  receipts: { initial_week_id: string | null; backup_week_id: string | null },
  inputsVersion: number
): Promise<WavePlanWeeksResponse> {
  const response = await api.put<WavePlanWeeksResponse>(
    `/wave-plans/${id}/core-receipts`,
    receipts,
    ifMatch(inputsVersion)
  )
  return response.data
}

export async function resetWavePlanWeeks(
  id: string,
  inputsVersion: number
): Promise<WavePlanWeeksResponse> {
  const response = await api.post<WavePlanWeeksResponse>(
    `/wave-plans/${id}/weeks/reset`,
    null,
    ifMatch(inputsVersion)
  )
  return response.data
}

export async function fetchWavePlanLines(
  id: string
): Promise<WavePlanLinesResponse> {
  const response = await api.get<WavePlanLinesResponse>(
    `/wave-plans/${id}/lines`
  )
  return response.data
}

export async function updateWavePlanLineWeeks(
  id: string,
  lineRefId: string,
  weeks: WavePlanSkuWeekInput[],
  inputsVersion: number
): Promise<WavePlanLinesResponse> {
  const response = await api.put<WavePlanLinesResponse>(
    `/wave-plans/${id}/lines/${lineRefId}/weeks`,
    { weeks },
    ifMatch(inputsVersion)
  )
  return response.data
}

export async function resetWavePlanLine(
  id: string,
  lineRefId: string,
  inputsVersion: number
): Promise<InputsVersionResponse> {
  const response = await api.delete<InputsVersionResponse>(
    `/wave-plans/${id}/lines/${lineRefId}/weeks`,
    ifMatch(inputsVersion)
  )
  return response.data
}

export async function resetWavePlanLines(
  id: string,
  inputsVersion: number
): Promise<InputsVersionResponse> {
  const response = await api.post<InputsVersionResponse>(
    `/wave-plans/${id}/lines/reset`,
    null,
    ifMatch(inputsVersion)
  )
  return response.data
}

export async function bulkWavePlanLines(
  id: string,
  body: WavePlanSkuBulkRequest,
  inputsVersion: number
): Promise<WavePlanSkuBulkResponse> {
  const response = await api.post<WavePlanSkuBulkResponse>(
    `/wave-plans/${id}/lines/bulk`,
    body,
    ifMatch(inputsVersion)
  )
  return response.data
}

export async function fetchWavePlanUndoStack(
  id: string
): Promise<WavePlanUndoStack> {
  const response = await api.get<WavePlanUndoStack>(
    `/wave-plans/${id}/undo-stack`
  )
  return response.data
}

/** Undo ``entryId`` (the edit the user confirmed); 409 when another edit is now the latest. */
export async function undoWavePlanChange(
  id: string,
  entryId: string,
  inputsVersion: number
): Promise<WavePlanUndoResult> {
  const response = await api.post<WavePlanUndoResult>(
    `/wave-plans/${id}/undo`,
    null,
    { ...ifMatch(inputsVersion), params: { entry_id: entryId } }
  )
  return response.data
}

export async function fetchWavePlanDc(
  id: string,
  weekId?: string | null
): Promise<WavePlanDcResponse> {
  const response = await api.get<WavePlanDcResponse>(
    `/wave-plans/${id}/distribution-centers`,
    { params: weekId ? { week_id: weekId } : undefined }
  )
  return response.data
}

export async function updateWavePlanDcSplit(
  id: string,
  lineRefId: string,
  weekId: string,
  splits: WavePlanDcSplitInput[],
  inputsVersion: number
): Promise<WavePlanDcResponse> {
  const response = await api.put<WavePlanDcResponse>(
    `/wave-plans/${id}/lines/${lineRefId}/weeks/${weekId}/dc-split`,
    { splits },
    ifMatch(inputsVersion)
  )
  return response.data
}

export async function bulkWavePlanDcSplits(
  id: string,
  body: WavePlanDcBulkRequest,
  inputsVersion: number
): Promise<WavePlanDcBulkResponse> {
  const response = await api.post<WavePlanDcBulkResponse>(
    `/wave-plans/${id}/dc-splits/bulk`,
    body,
    ifMatch(inputsVersion)
  )
  return response.data
}

export async function resetWavePlanDcSplits(
  id: string,
  inputsVersion: number
): Promise<InputsVersionResponse> {
  const response = await api.delete<InputsVersionResponse>(
    `/wave-plans/${id}/dc-splits`,
    ifMatch(inputsVersion)
  )
  return response.data
}

const XLSX_TYPE =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

/**
 * ``wave-plan-<AP name>-<YYYY-MM-DD>.xlsx`` with the server's safe characters,
 * for when the attachment's filename can't be read.
 */
function exportFilename(apName: string | null | undefined): string {
  const trim = (value: string) => value.replace(/^[-._]+|[-._]+$/g, '')
  const slug = trim(
    trim((apName ?? '').replace(/[^A-Za-z0-9._-]+/g, '-')).slice(0, 80)
  )
  return `wave-plan-${slug || 'plan'}-${new Date().toISOString().slice(0, 10)}.xlsx`
}

/** The attachment's filename, when the header is readable (CORS may hide it). */
function attachmentFilename(header: unknown): string | null {
  if (typeof header !== 'string') return null
  const encoded = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(header)
  if (encoded) {
    try {
      return decodeURIComponent(encoded[1].trim())
    } catch {
      // Malformed: try the plain filename.
    }
  }
  return /filename\s*=\s*"?([^";]+)"?/i.exec(header)?.[1].trim() || null
}

/**
 * Downloads the plan's .xlsx export: the melted DC allocation,
 * SKU waves, POs by vendor and DC, flags and parameters. Returns the filename.
 */
export async function downloadWavePlanExport(
  id: string,
  apName?: string | null
): Promise<string> {
  let response
  try {
    response = await api.get<Blob>(`/wave-plans/${id}/export`, {
      responseType: 'blob',
    })
  } catch (error) {
    // A blob error body hides the problem detail the toast shows.
    if (error instanceof AxiosError && error.response?.data instanceof Blob) {
      try {
        error.response.data = JSON.parse(await error.response.data.text())
      } catch {
        // Not JSON: the toast falls back to its own message.
      }
    }
    throw error
  }
  const filename =
    attachmentFilename(response.headers['content-disposition']) ??
    exportFilename(apName)
  const blob = new Blob([response.data], { type: XLSX_TYPE })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
  return filename
}

export async function previewWavePlanRefresh(
  id: string
): Promise<WavePlanRefreshPreview> {
  const response = await api.get<WavePlanRefreshPreview>(
    `/wave-plans/${id}/refresh/preview`
  )
  return response.data
}

export async function refreshWavePlan(
  id: string,
  inputsVersion: number
): Promise<WavePlanRefreshResponse> {
  const response = await api.post<WavePlanRefreshResponse>(
    `/wave-plans/${id}/refresh`,
    null,
    ifMatch(inputsVersion)
  )
  return response.data
}

export async function submitWavePlan(
  id: string,
  body: { reviewer_ids: string[]; notes?: string | null }
): Promise<WavePlanSubmission> {
  const response = await api.post<WavePlanSubmission>(
    `/wave-plans/${id}/submissions`,
    body
  )
  return response.data
}

export async function withdrawWavePlanSubmission(
  id: string,
  submissionId: string
): Promise<WavePlanSubmission> {
  const response = await api.delete<WavePlanSubmission>(
    `/wave-plans/${id}/submissions/${submissionId}`
  )
  return response.data
}

export type WavePlanDecision = 'approve' | 'send-back' | 'unapprove'

export async function decideWavePlanSubmission(
  submissionId: string,
  decision: WavePlanDecision,
  decisionNotes?: string | null
): Promise<WavePlanSubmission> {
  const response = await api.post<WavePlanSubmission>(
    `/manager/wave-plan-submissions/${submissionId}/${decision}`,
    { decision_notes: decisionNotes ?? null }
  )
  return response.data
}

export async function fetchWavePlanRollup(
  ids: string[]
): Promise<WavePlanRollupResponse> {
  const response = await api.get<WavePlanRollupResponse>('/wave-plans/rollup', {
    params: { ids: ids.join(',') },
  })
  return response.data
}

export async function fetchWavePlanLineComments(
  id: string,
  lineRefId: string
): Promise<PaginatedCommentsResponse> {
  const response = await api.get<PaginatedCommentsResponse>(
    `/wave-plans/${id}/lines/${lineRefId}/comments`,
    { params: { offset: 0, limit: 200 } }
  )
  return response.data
}

export async function createWavePlanLineComment(
  id: string,
  lineRefId: string,
  content: string
): Promise<CommentRead> {
  const response = await api.post<CommentRead>(
    `/wave-plans/${id}/lines/${lineRefId}/comments`,
    { content }
  )
  return response.data
}

export async function deleteWavePlanComment(commentId: string): Promise<void> {
  await api.delete(`/comments/${commentId}`)
}
